import { ObservableItemIdentifier } from '../../api-contracts/other/ObservableItemIdentifier';
import DatabaseAssetsMethods from '../../data/database.assets.methods';
import { inspectionDB } from '../../data/database.structure';
import {
  EquipmentConsts,
  EquipmentType,
  ObservableLevelsEnum,
} from '../../data/enums';
import {
  EquipmentModel,
  EquipmentPartModel,
  InspectionCategoryTypeModel,
  ObservationModel,
  PartManufacturerModel,
} from '../../data/types';
import { observationService } from '../observationService';
import { observationDraftService } from '../observationDraftService';
import { inspectionRecordService } from '../inspection/inspectionRecordService';

/** #10285: the family this ticket's purge owns, in one place. */
const BUCKET_IDENTIFIER_PREFIX = 'Bucket.';
const isBucketPath = (identifier: string) =>
  identifier.startsWith(BUCKET_IDENTIFIER_PREFIX);
const isBucketObservation = (observation: ObservationModel) =>
  isBucketPath(observation.observableItem.identifier);

/**
 * #10285: change one inspection category type's observations on the current record,
 * through the Inspection record service. `mutate` returns the rows it considers changed
 * plus the list to store; returning no changed rows skips the write entirely.
 *
 * Shared by the purge and its undo so the two cannot drift apart - the drift between
 * their two draft-layer contracts is what produced the review findings on this PR.
 */
const mutateInspectionCategoryObservations = async (
  inspectionId: string,
  inspectionCategoryId: string,
  mutate: (categoryType: InspectionCategoryTypeModel) => {
    changed: ObservationModel[];
    observations: ObservationModel[];
  },
): Promise<ObservationModel[]> => {
  let changed: ObservationModel[] = [];
  await inspectionRecordService.change(inspectionId, (inspection) => {
    const categoryType = inspection.inspectionCategoryTypes.find(
      (category) => category.externalId === inspectionCategoryId,
    );
    if (!categoryType) return;
    const result = mutate(categoryType);
    changed = result.changed;
    if (changed.length > 0) categoryType.observations = result.observations;
  });
  return changed;
};

/**
 * #10285: settle up with the draft layer after writing `Bucket.*` rows directly.
 *
 * Every dirty draft for the family is dropped, not just the ones that had a row on disk.
 * A draft whose flush never landed (offline, or any failed `ObservationDraftScope` unmount
 * flush - that path skips `release()`) has no row for a `supersede()` to key off, so it
 * would survive the write and then re-create its row on the next `flushAll()` before sync,
 * putting back the duplicate `Bucket.ExternalId` this ticket removes.
 *
 * Discarding also leaves the draft layer with nothing to mirror, so fields re-seed from
 * disk on next mount: empty after a purge, the restored value after an undo. That avoids
 * seeding a collection path as a non-passthrough draft entry, which would route a
 * collection observable through the draft pipeline instead of the legacy immediate write.
 */
const discardBucketDrafts = (inspectionId: string) =>
  observationDraftService.discardMatching(inspectionId, isBucketPath);

interface BucketFormData {
  bucketExternalId: string;
  bucketInstallDate: string;
  bucketMachineHours: number;
  bucketCapitalIndexNumber: string;
  bucketName: string;
  bucketFitment: number;
  bucketManufacturer: string;
  bucketPartNumber: string;
  bucketSerialNumber: string;
}

interface NewBucketProps {
  inspectionId: string;
  inspectionCategoryId: string;
  mineLocationId?: number;
  mineLocationExternalId?: string;
  assetId?: number;
  assetExternalId?: string;
  equipmentId: number;
  equipmentExternalId: string;
  oldBucketId?: string;
  formData: BucketFormData;
}

interface UpdateBucketProps {
  inspectionId: string;
  inspectionCategoryId: string;
  equipmentId: number;
  equipmentExternalId: string;
  formData: Partial<BucketFormData>;
}

class EquipmentService {
  async createBucket({
    bucketExternalId,
    equipmentConfiguration,
    effectiveDate,
    installDate,
    equipmentParts,
    formData,
  }: {
    bucketExternalId: string;
    equipmentConfiguration: number;
    effectiveDate: Date;
    installDate: Date;
    equipmentParts: EquipmentPartModel[];
    formData?: {
      bucketName: string;
      manufacturer: number;
      machine?: number;
      mineLocation?: number;
      serialNumber: string;
      bucketPartNumber: string;
      bucketCINumber: string;
    };
  }): Promise<void> {
    const equipment: EquipmentModel = {
      id: 0,
      externalId: bucketExternalId,
      name: formData?.bucketName ?? '',
      assetId: formData?.machine ?? -1,
      mineLocationId: formData?.mineLocation ?? -1,
      description: '',
      equipmentCategory:
        EquipmentConsts.EquipmentCategories[EquipmentType.bucket].name,
      equipmentCategoryId:
        EquipmentConsts.EquipmentCategories[EquipmentType.bucket].id,
      equipmentType: EquipmentConsts.EquipmentTypes[EquipmentType.bucket].name,
      equipmentTypeId: EquipmentConsts.EquipmentTypes[EquipmentType.bucket].id,
      configurationId: equipmentConfiguration,
      effectiveDate: effectiveDate,
      installDate: installDate,
      manufacturerId: formData?.manufacturer ?? -1,
      parentAssetId: null,
      equipmentParts: equipmentParts.filter(
        (part) => part !== null,
      ) as EquipmentPartModel[],
      serialNumber: formData?.serialNumber ?? '',
      partNumber: formData?.bucketPartNumber ?? '',
      capitalIndexNumber: formData?.bucketCINumber ?? '',
    };

    const assetMethods = new DatabaseAssetsMethods(inspectionDB);
    await assetMethods.addEqupmentToAsset(equipment);
  }

  async updateBucket({
    inspectionId,
    inspectionCategoryId,
    equipmentId,
    equipmentExternalId,
    formData,
    equipmentParts,
  }: UpdateBucketProps & {
    equipmentParts: EquipmentPartModel[];
  }): Promise<void> {
    let manufacturer: PartManufacturerModel | undefined = undefined;
    if (formData?.bucketManufacturer) {
      manufacturer = await inspectionDB.assetManufacturers
        .where('name')
        .equals(formData.bucketManufacturer)
        .first();
    }

    const installDate = formData?.bucketInstallDate
      ? new Date(formData.bucketInstallDate)
      : new Date();

    // Update Equipment details
    await inspectionDB.equipments.update(equipmentExternalId, {
      name: formData.bucketName,
      installDate: installDate,
      effectiveDate: installDate,
      capitalIndexNumber: formData?.bucketCapitalIndexNumber,
      partNumber: formData?.bucketPartNumber,
      serialNumber: formData?.bucketSerialNumber,
      manufacturerId: manufacturer?.id,
    });

    // Update Equipment Parts effective date
    await Promise.all(
      equipmentParts.map(async (part) => {
        await inspectionDB.equipmentParts.update(part.externalId, {
          effectiveDate: installDate,
        });
      }),
    );
  }

  /**
   * #10285: a changeout must not leave the outgoing bucket's `Bucket.*` observations in the
   * inspection. Left behind they ship in the submit payload against a bucket the same request
   * detaches (`sanitizeObservationsForWire` dedupes by a tuple that includes
   * `entityExternalId`, so they are never dropped); they let the outgoing bucket win the
   * server's `EquipmentMap` (`InspectionSyncService` assigns rather than appends, and the FE
   * pushes the outgoing bucket second), which leaves the incoming bucket's `entityId: -1` rows
   * unmapped; and a duplicate `Bucket.ExternalId` makes `database.assets.methods` resolve the
   * changeout-to bucket as the OUTGOING one via `obs.find`, so the UI shows the old bucket's
   * name and specifications.
   *
   * The filter is deliberately identical to `BucketRotationScreen`'s: every observation whose
   * identifier starts with `Bucket.`, at any data level, for any bucket. Per #10285 AC 1
   * ("no `Bucket.*` observations referencing the old bucket remain ... matching existing
   * Rotation behaviour") and AC 2 ("Wear-kit/GET observations recorded against the old
   * bucket's screens are handled the same way Rotation handles them today").
   *
   * DO NOT NARROW THIS FILTER. The wide blast radius is the decision, not an oversight:
   * `Bucket.` matches the whole bucket inspection, not just the identity rows. It removes
   * every equipmentPart-level GET and wear-kit measurement, the wear indicators, the GET /
   * wear-kit / structural / bucket summary comments and photos, the completion flags, and the
   * per-position GET change-out declarations - and because `buildGetChangeOutSyncItems` builds
   * the typed `getChangeOuts` sync records from those `Bucket.GroundEngagingTools.ChangeOut.*`
   * rows at submit time, the typed change-out events go with them. That is intended: an
   * inspection describes exactly ONE bucket (`getInspectionAssetCurrentEquipment` re-points it
   * at the changeout-to bucket, every GET screen is driven by that bucket's `equipmentParts`,
   * and the server's `EquipmentMap` is a single-element list), so once the changeout is
   * declared the previous bucket's inspection content is void - it would otherwise ship and
   * persist while being invisible on every screen and in the report. A narrower, identity-only
   * purge fixes the defect but leaves exactly that unreviewable residue behind.
   *
   * Because it is not keyed to a named outgoing bucket, repeat changeouts in one inspection
   * are handled too: `NewBucket` resolves the outgoing bucket with `e.id > 0`, and a bucket
   * created earlier in the same inspection carries `id: 0`, so a bucket-specific filter would
   * have missed it. Safe to run before `createNewBucketObservations`, which writes the
   * incoming bucket's rows immediately afterwards.
   *
   * This is the shared implementation the ticket's suggested fix asked for. It is used by
   * `NewBucket` and `NewBlankBucket` only: `BucketRotationScreen` deliberately keeps its own
   * inline copy, because AC 3 requires its behaviour unchanged and that screen has a separate
   * open defect (rotating back to the original bucket clears every `Bucket.*` row and restores
   * none of them). Folding it onto this function is a behaviour-preserving refactor, but it
   * belongs to that Rotation ticket rather than this one.
   *
   * @returns the observations that were removed, so the caller can put them back if a
   * later step of the changeout fails. Empty when there was nothing to purge.
   */
  async purgeBucketObservations({
    inspectionId,
    inspectionCategoryId,
  }: {
    inspectionId: string;
    inspectionCategoryId: string;
  }): Promise<ObservationModel[]> {
    const removed = await mutateInspectionCategoryObservations(
      inspectionId,
      inspectionCategoryId,
      (categoryType) => {
        const changed: ObservationModel[] = [];
        const observations: ObservationModel[] = [];
        for (const observation of categoryType.observations) {
          (isBucketObservation(observation) ? changed : observations).push(
            observation,
          );
        }
        return { changed, observations };
      },
    );

    // Unconditional: a dirty draft that never reached disk is not in `removed`, and it is
    // exactly the case that would resurrect a purged row. See discardBucketDrafts.
    discardBucketDrafts(inspectionId);

    return removed;
  }

  /**
   * #10285: undo a `purgeBucketObservations` when the changeout it was clearing the way
   * for did not complete. `NewBucket` purges before `createBucket` /
   * `createNewBucketObservations`, and those can throw (a failed
   * `addEqupmentToAsset`, an unresolvable observable item). Without this the inspector
   * is returned to the form with the whole prior bucket inspection - measurements,
   * comments, photos - already deleted and no changeout recorded in exchange.
   *
   * Rows already present are skipped, so restoring twice cannot duplicate them.
   */
  async restoreBucketObservations({
    inspectionId,
    inspectionCategoryId,
    observations,
  }: {
    inspectionId: string;
    inspectionCategoryId: string;
    observations: ObservationModel[];
  }): Promise<ObservationModel[]> {
    if (observations.length === 0) {
      return [];
    }

    // Identity of a stored row for "is it already back?": level, entity and path. Unit is
    // metadata rather than identity for a non-collection row, the same assumption the
    // draft layer's tombstone deletion makes.
    const identityOf = (observation: ObservationModel) =>
      [
        observation.dataLevelId,
        observation.entityExternalId,
        observation.observableItem.identifier,
      ].join('|');

    const restored = await mutateInspectionCategoryObservations(
      inspectionId,
      inspectionCategoryId,
      (categoryType) => {
        const present = new Set(categoryType.observations.map(identityOf));
        const toRestore = observations.filter(
          (observation) => !present.has(identityOf(observation)),
        );
        return {
          changed: toRestore,
          observations: [...categoryType.observations, ...toRestore],
        };
      },
    );

    // Same contract as the purge: drop the family's drafts rather than mirroring values
    // into them. Fields then re-seed from disk and read the restored rows, and no draft
    // entry is invented for a collection path (which would route it as draft mode).
    discardBucketDrafts(inspectionId);

    return restored;
  }

  async createNewBucketObservations({
    inspectionId,
    inspectionCategoryId,
    mineLocationId,
    mineLocationExternalId,
    assetId,
    assetExternalId,
    equipmentId,
    equipmentExternalId,
    oldBucketId,
    formData,
  }: NewBucketProps): Promise<boolean> {
    // If there is a mine location or asset, we can continue
    const hasMineLocation =
      mineLocationId !== undefined && mineLocationExternalId !== undefined;
    const hasAsset = assetId !== undefined && assetExternalId !== undefined;
    if (!hasMineLocation && !hasAsset) {
      throw new Error('Mine location and asset information are required.');
    }

    if (hasMineLocation && hasAsset) {
      throw new Error(
        'Both mine location and asset information cannot be provided at the same time.',
      );
    }

    await observationService.deleteObservationByPath({
      inspectionId: inspectionId,
      inspectionCategoryId: inspectionCategoryId,
      path: ObservableItemIdentifier.Identifiers.Bucket.IsChangeOut,
    });

    await observationService.deleteObservationByPath({
      inspectionId: inspectionId,
      inspectionCategoryId: inspectionCategoryId,
      path: ObservableItemIdentifier.Identifiers.Bucket.ChangeOutFrom,
    });

    // Asset/Mine Location Observations
    const assetMineLocationDataLevel = mineLocationId
      ? ObservableLevelsEnum.mineLocation
      : ObservableLevelsEnum.asset;

    await observationService.upsertObservation({
      dataLevel: assetMineLocationDataLevel,
      entityId: assetId ?? mineLocationId!,
      entityExternalId: assetExternalId ?? mineLocationExternalId!,
      inspectionId: inspectionId,
      inspectionCategoryId: inspectionCategoryId,
      path: ObservableItemIdentifier.Identifiers.Bucket.IsChangeOut,
      value: 'true',
    });

    // Equipment Observations
    //
    // #10290: these are NOT read by the API. The server populates the equipment
    // record exclusively from `equipmentItems` (EquipmentMappingProfile ->
    // InspectionSyncRepository.HandleEquipmentItemsAsync), and the only Bucket.*
    // identifier the backend consumes is Bucket.ExternalId.
    //
    // They exist for a FRONTEND round-trip: joinBucketObservationData
    // (database.assets.methods.ts) folds them back onto the bucket, and
    // inspectionPushService pushes that bucket as `equipmentItems`. The rotation
    // flow calls this method WITHOUT createBucket/updateBucket, so for a rotation
    // these observations are the ONLY path by which the values reach the pushed
    // equipment row.
    //
    // Exception: Bucket.Installation.MachineHours is NOT folded by
    // joinBucketObservationData - it round-trips instead via the bound form
    // fields on BucketRotationScreen and BucketSpecificationsScreen.
    //
    // Do not "de-duplicate" them against equipmentItems. The identifiers that
    // genuinely were dead (Bucket.Location.*, Bucket.IsNew) are already removed;
    // what remains is load-bearing. See
    // src/__tests__/bucketChangeoutObservationWrites.test.ts, which pins the set.
    //
    // One deliberate exception: Bucket.Specifications.Fitment has no reader that
    // could be found - it duplicates EquipmentModel.configurationId, which is what
    // BucketSpecificationsScreen actually hydrates the GET Fitment from. #10290
    // scoped this identifier as "must keep", so it stays. Retiring it needs its
    // own ticket, not a drive-by deletion.

    if (oldBucketId) {
      await observationService.upsertObservation({
        dataLevel: ObservableLevelsEnum.equipment,
        entityId: equipmentId,
        entityExternalId: equipmentExternalId,
        inspectionCategoryId: inspectionCategoryId,
        inspectionId: inspectionId,
        path: ObservableItemIdentifier.Identifiers.Bucket.ChangeOutFrom,
        value: oldBucketId!,
      });
    }

    await observationService.upsertObservation({
      dataLevel: ObservableLevelsEnum.equipment,
      entityId: equipmentId,
      entityExternalId: equipmentExternalId,
      inspectionCategoryId: inspectionCategoryId,
      inspectionId: inspectionId,
      path: ObservableItemIdentifier.Identifiers.Bucket.ExternalId,
      value: formData.bucketExternalId,
    });

    await observationService.upsertObservation({
      dataLevel: ObservableLevelsEnum.equipment,
      entityId: equipmentId,
      entityExternalId: equipmentExternalId,
      inspectionCategoryId: inspectionCategoryId,
      inspectionId: inspectionId,
      path: ObservableItemIdentifier.Identifiers.Bucket.Installation
        .InstallDate,
      value: formData.bucketInstallDate,
    });

    await observationService.upsertObservation({
      dataLevel: ObservableLevelsEnum.equipment,
      entityId: equipmentId,
      entityExternalId: equipmentExternalId,
      inspectionCategoryId: inspectionCategoryId,
      inspectionId: inspectionId,
      path: ObservableItemIdentifier.Identifiers.Bucket.Installation
        .MachineHours,
      value: formData.bucketMachineHours.toString(),
    });

    await observationService.upsertObservation({
      dataLevel: ObservableLevelsEnum.equipment,
      entityId: equipmentId,
      entityExternalId: equipmentExternalId,
      inspectionCategoryId: inspectionCategoryId,
      inspectionId: inspectionId,
      path: ObservableItemIdentifier.Identifiers.Bucket.Specifications
        .CapitalIndexNumber,
      value: formData.bucketCapitalIndexNumber,
    });

    await observationService.upsertObservation({
      dataLevel: ObservableLevelsEnum.equipment,
      entityId: equipmentId,
      entityExternalId: equipmentExternalId,
      inspectionCategoryId: inspectionCategoryId,
      inspectionId: inspectionId,
      path: ObservableItemIdentifier.Identifiers.Bucket.Specifications
        .CustomerBucketName,
      value: formData.bucketName,
    });

    await observationService.upsertObservation({
      dataLevel: ObservableLevelsEnum.equipment,
      entityId: equipmentId,
      entityExternalId: equipmentExternalId,
      inspectionCategoryId: inspectionCategoryId,
      inspectionId: inspectionId,
      path: ObservableItemIdentifier.Identifiers.Bucket.Specifications.Fitment,
      value: formData.bucketFitment.toString(),
    });

    await observationService.upsertObservation({
      dataLevel: ObservableLevelsEnum.equipment,
      entityId: equipmentId,
      entityExternalId: equipmentExternalId,
      inspectionCategoryId: inspectionCategoryId,
      inspectionId: inspectionId,
      path: ObservableItemIdentifier.Identifiers.Bucket.Specifications
        .Manufacturer,
      value: formData.bucketManufacturer,
    });

    await observationService.upsertObservation({
      dataLevel: ObservableLevelsEnum.equipment,
      entityId: equipmentId,
      entityExternalId: equipmentExternalId,
      inspectionCategoryId: inspectionCategoryId,
      inspectionId: inspectionId,
      path: ObservableItemIdentifier.Identifiers.Bucket.Specifications
        .PartNumber,
      value: formData.bucketPartNumber,
    });

    await observationService.upsertObservation({
      dataLevel: ObservableLevelsEnum.equipment,
      entityId: equipmentId,
      entityExternalId: equipmentExternalId,
      inspectionCategoryId: inspectionCategoryId,
      inspectionId: inspectionId,
      path: ObservableItemIdentifier.Identifiers.Bucket.Specifications
        .SerialNumber,
      value: formData.bucketSerialNumber,
    });

    return true;
  }

  async updateBucketDetailsObservations({
    inspectionId,
    inspectionCategoryId,
    equipmentId,
    equipmentExternalId,
    formData,
  }: UpdateBucketProps): Promise<boolean> {
    const entityIdAdjusted = equipmentId === 0 ? -1 : equipmentId;

    // Equipment Observations
    if (formData.bucketExternalId) {
      await observationService.upsertObservation({
        dataLevel: ObservableLevelsEnum.equipment,
        entityId: entityIdAdjusted,
        entityExternalId: equipmentExternalId,
        inspectionCategoryId: inspectionCategoryId,
        inspectionId: inspectionId,
        path: ObservableItemIdentifier.Identifiers.Bucket.ExternalId,
        value: formData.bucketExternalId,
      });
    }

    if (formData.bucketInstallDate) {
      await observationService.upsertObservation({
        dataLevel: ObservableLevelsEnum.equipment,
        entityId: entityIdAdjusted,
        entityExternalId: equipmentExternalId,
        inspectionCategoryId: inspectionCategoryId,
        inspectionId: inspectionId,
        path: ObservableItemIdentifier.Identifiers.Bucket.Installation
          .InstallDate,
        value: formData.bucketInstallDate,
      });
    }

    if (formData.bucketMachineHours) {
      await observationService.upsertObservation({
        dataLevel: ObservableLevelsEnum.equipment,
        entityId: entityIdAdjusted,
        entityExternalId: equipmentExternalId,
        inspectionCategoryId: inspectionCategoryId,
        inspectionId: inspectionId,
        path: ObservableItemIdentifier.Identifiers.Bucket.Installation
          .MachineHours,
        value: formData.bucketMachineHours.toString(),
      });
    }

    if (formData.bucketCapitalIndexNumber) {
      await observationService.upsertObservation({
        dataLevel: ObservableLevelsEnum.equipment,
        entityId: entityIdAdjusted,
        entityExternalId: equipmentExternalId,
        inspectionCategoryId: inspectionCategoryId,
        inspectionId: inspectionId,
        path: ObservableItemIdentifier.Identifiers.Bucket.Specifications
          .CapitalIndexNumber,
        value: formData.bucketCapitalIndexNumber,
      });
    }

    if (formData.bucketName) {
      await observationService.upsertObservation({
        dataLevel: ObservableLevelsEnum.equipment,
        entityId: entityIdAdjusted,
        entityExternalId: equipmentExternalId,
        inspectionCategoryId: inspectionCategoryId,
        inspectionId: inspectionId,
        path: ObservableItemIdentifier.Identifiers.Bucket.Specifications
          .CustomerBucketName,
        value: formData.bucketName,
      });
    }

    if (formData.bucketManufacturer) {
      await observationService.upsertObservation({
        dataLevel: ObservableLevelsEnum.equipment,
        entityId: entityIdAdjusted,
        entityExternalId: equipmentExternalId,
        inspectionCategoryId: inspectionCategoryId,
        inspectionId: inspectionId,
        path: ObservableItemIdentifier.Identifiers.Bucket.Specifications
          .Manufacturer,
        value: formData.bucketManufacturer,
      });
    }

    if (formData.bucketPartNumber) {
      await observationService.upsertObservation({
        dataLevel: ObservableLevelsEnum.equipment,
        entityId: entityIdAdjusted,
        entityExternalId: equipmentExternalId,
        inspectionCategoryId: inspectionCategoryId,
        inspectionId: inspectionId,
        path: ObservableItemIdentifier.Identifiers.Bucket.Specifications
          .PartNumber,
        value: formData.bucketPartNumber,
      });
    }

    if (formData.bucketSerialNumber) {
      await observationService.upsertObservation({
        dataLevel: ObservableLevelsEnum.equipment,
        entityId: entityIdAdjusted,
        entityExternalId: equipmentExternalId,
        inspectionCategoryId: inspectionCategoryId,
        inspectionId: inspectionId,
        path: ObservableItemIdentifier.Identifiers.Bucket.Specifications
          .SerialNumber,
        value: formData.bucketSerialNumber,
      });
    }

    return true;
  }
}

export const equipmentService = new EquipmentService();
